package com.nonopn.skywatcher.common.app.portfolio.selected

import androidx.compose.material.ScaffoldState
import com.nonopn.skywatcher.app.utils.safeLaunch
import com.nonopn.skywatcher.common.app.api.impl.ApiInterface
import com.nonopn.skywatcher.common.app.api.impl.ApiInterfaceFake
import com.nonopn.skywatcher.common.app.navigation.RouteDetection
import com.nonopn.skywatcher.common.app.pages.sentinels.ThunderDetection
import com.nonopn.skywatcher.common.app.pages.sentinels.ThunderDetectionWithMetadata
import com.nonopn.skywatcher.common.app.res.Res
import com.nonopn.skywatcher.common.app.res.app_title_client
import com.nonopn.skywatcher.common.app.res.app_title_manager
import com.nonopn.skywatcher.common.app.res.warn_customer_text_client
import com.nonopn.skywatcher.common.app.res.warn_customer_text_manager
import com.nonopn.skywatcher.design.appbar.AppBarState
import com.nonopn.skywatcher.design.appbar.AppBarStateProvider
import com.nonopn.skywatcher.design.appbar.FloatingActionButtonState
import eu.codlab.navigation.NavigateTo
import eu.codlab.navigation.NavigationListener
import eu.codlab.navigation.NavigatorModel
import eu.codlab.viewmodel.StateViewModel
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import kotlinx.serialization.Serializable
import org.jetbrains.compose.resources.StringResource

private const val Millis = 1000.0

@Suppress("TooManyFunctions")
abstract class ThunderDetectionSelectedModel(
    val isSystemDarkMode: Boolean,
    private val currentApi: ApiInterface,
    private val selectedThunderDetection: ThunderDetectionWithMetadata,
) : StateViewModel<ThunderDetectionSelectedModelState>(
    ThunderDetectionSelectedModelState(
        RouteDetection(selectedThunderDetection.detection.uuid),
        isSystemDarkMode = isSystemDarkMode,
        currentApi = currentApi,
        selectedThunderDetection = selectedThunderDetection
    )
), AppBarStateProvider, NavigationListener {
    var mainCompositionScope: CoroutineScope? = null
    var navigator: NavigatorModel? = null
    var scaffoldState: ScaffoldState? = null

    abstract val warnCustomerText: StringResource

    abstract val appTitle: StringResource

    companion object {
        fun fake(): ThunderDetectionSelectedModel {
            return object : ThunderDetectionSelectedModel(
                false,
                ApiInterfaceFake,
                ThunderDetection.fakes.first()
            ) {
                override val warnCustomerText: StringResource
                    get() = Res.string.warn_customer_text_client

                override val appTitle: StringResource
                    get() = Res.string.app_title_manager

                override fun warnCustomerAction() {
                    // nothing
                }
            }
        }
    }

    abstract fun warnCustomerAction()

    override fun setAppBarState(appBarState: AppBarState) {
        safeLaunch {
            updateState {
                copy(appBarState = appBarState)
            }
        }
    }

    fun setFloatingActionButton(floatingActionButtonState: FloatingActionButtonState?) =
        safeLaunch {
            updateState {
                copy(floatingActionButtonState = floatingActionButtonState)
            }
        }

    fun popBackStack() {
        navigator?.navigateUp()
    }

    fun floatingActionButton(): FloatingActionButtonState? {
        return states.value.floatingActionButtonState
    }

    fun show(navigateTo: NavigateTo) {
        if (navigateTo.stack.popBackStack) {
            popBackStack()
        }

        navigator?.navigateTo(navigateTo)

        mainCompositionScope?.launch {
            try {
                scaffoldState?.drawerState?.close()
            } catch (err: Throwable) {
                err.printStackTrace()
            }
        }

        updateState {
            copy(
                appBarState = AppBarState.Regular(),
                floatingActionButtonState = null
            )
        }
    }

    override fun shown(navigateTo: NavigateTo) {
        // nothing yet but should be using the above instead ?
    }
}

class ThunderDetectionSelectedModelManager(
    isSystemDarkMode: Boolean,
    thunderDetection: ThunderDetectionWithMetadata,
    currentApi: ApiInterface
) :
    ThunderDetectionSelectedModel(isSystemDarkMode, currentApi, thunderDetection) {
    override val warnCustomerText: StringResource
        get() = Res.string.warn_customer_text_manager

    override val appTitle: StringResource
        get() = Res.string.app_title_manager

    override fun warnCustomerAction() = Unit
}

class ThunderDetectionSelectedModelCustomer(
    isSystemDarkMode: Boolean,
    thunderDetection: ThunderDetectionWithMetadata,
    currentApi: ApiInterface
) :
    ThunderDetectionSelectedModel(isSystemDarkMode, currentApi, thunderDetection) {
    override val warnCustomerText: StringResource
        get() = Res.string.warn_customer_text_client

    override val appTitle: StringResource
        get() = Res.string.app_title_client

    override fun warnCustomerAction() = Unit
}

@Serializable
data class SpecificData(
    val nothing: Boolean = false
)